import JSON5 from "json5";

type ParsedJson = object | unknown[];

// Tagged-union view of a tool-call args or result string. `parseToolBody`
// always returns one of these, never throws — every JSON parse is wrapped
// and every branch returns a value.
export type ToolView =
  | { kind: "empty" }
  | { kind: "text"; text: string }
  | { kind: "json"; value: ParsedJson }
  | { kind: "instructions"; text: string; rest?: ParsedJson };

function tryParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    /* fall through to JSON5 */
  }
  try {
    return JSON5.parse(raw);
  } catch {
    return undefined;
  }
}

// Some backends wrap their argument payload as `{ raw: "<stringified JSON>" }`.
// If we see exactly that shape, unwrap it — recursively, because the inner
// string may itself be a wrapper. Stops as soon as the shape no longer matches.
function unwrapRaw(value: unknown): unknown {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "raw" in value &&
    Object.keys(value as object).length === 1
  ) {
    const inner = (value as { raw: unknown }).raw;
    if (typeof inner === "string") {
      const parsed = tryParse(inner);
      return parsed !== undefined ? unwrapRaw(parsed) : inner;
    }
  }
  return value;
}

export function parseToolBody(raw: string | null | undefined): ToolView {
  if (raw == null) return { kind: "empty" };
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "empty" };

  const parsed = tryParse(trimmed);

  // Not valid JSON/JSON5 → render the original (untrimmed) text verbatim
  // so we don't lose leading/trailing whitespace the caller chose to keep.
  if (parsed === undefined) {
    return { kind: "text", text: raw };
  }

  const unwrapped = unwrapRaw(parsed);

  if (typeof unwrapped === "string") {
    return { kind: "text", text: unwrapped };
  }
  if (
    unwrapped !== null &&
    typeof unwrapped === "object" &&
    !Array.isArray(unwrapped) &&
    "instructions" in unwrapped &&
    typeof (unwrapped as { instructions: unknown }).instructions === "string"
  ) {
    const { instructions, ...rest } = unwrapped as {
      instructions: string;
      [k: string]: unknown;
    };
    const hasRest = Object.keys(rest).length > 0;
    return {
      kind: "instructions",
      text: instructions,
      rest: hasRest ? (rest as ParsedJson) : undefined,
    };
  }
  if (typeof unwrapped === "object" && unwrapped !== null) {
    return { kind: "json", value: unwrapped as ParsedJson };
  }
  return { kind: "text", text: String(unwrapped) };
}
